import type { JawOutline } from '@witmetrics/utils';
import Dialog, {
  DefaultDialogActions,
  DialogBody,
  DialogTitle,
} from '@/components/Dialog';
import Table, {
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  TableRow,
} from '@/components/Table';
import { LABELS, TEETH } from './constants';
import { buildRows, classes } from './utils';

export type JawOutlineDialogProps = {
  title?: string;
  settings: JawOutline;
  onClose: () => void;
};

export default function JawOutlineDialog({
  title = 'Jaw Outline',
  settings,
  onClose,
}: JawOutlineDialogProps) {
  const renderTableHead = () => {
    return (
      <TableHead>
        <TableHeadRow>
          <TableHeadCell>Tooth</TableHeadCell>
          {TEETH.map((t) => (
            <TableHeadCell key={t} className={classes.cell}>
              {LABELS[t]}
            </TableHeadCell>
          ))}
        </TableHeadRow>
      </TableHead>
    );
  };

  const renderTableBody = (location: 'buccal' | 'lingual') => {
    const rows = buildRows(settings[location]);
    return (
      <TableBody>
        <TableRow>
          <TableCell>Type</TableCell>
          {rows.types.map((t, i) => (
            <TableCell key={i} className={classes.cell}>
              {t}
            </TableCell>
          ))}
        </TableRow>
        <TableRow>
          <TableCell>Outline Height</TableCell>
          {rows.heights.map((h, i) => (
            <TableCell key={i} className={classes.cell}>
              {h}
            </TableCell>
          ))}
        </TableRow>
        <TableRow>
          <TableCell>Outline Extension (mm)</TableCell>
          {rows.offsets.map((o, i) => (
            <TableCell key={i} className={classes.cell}>
              {o}
            </TableCell>
          ))}
        </TableRow>
      </TableBody>
    );
  };

  return (
    <Dialog className={classes.dialog} onClose={onClose}>
      <DialogTitle title={title} onClose={onClose} />
      <DialogBody className={classes.body}>
        <div>
          <h1 className={classes.title}>Buccal</h1>
          <Table>
            {renderTableHead()}
            {renderTableBody('buccal')}
          </Table>
        </div>
        <div>
          <h1 className={classes.title}>Lingual</h1>
          <Table>
            {renderTableHead()}
            {renderTableBody('lingual')}
          </Table>
        </div>
        <div className={classes.excludeMolar}>
          <b>Exclude Last Half Molar: </b>
          <span>
            {settings.excludeLastHalfMolarFromOutline ? 'True' : 'False'}
          </span>
        </div>
      </DialogBody>
      <DefaultDialogActions
        confirmLabel="Done"
        onCancel={onClose}
        onConfirm={onClose}
      />
    </Dialog>
  );
}
